import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Table,
  Row,
  Col,
  Badge,
  Spinner,
} from 'reactstrap';
import { translate } from 'react-translate';
import classnames from 'classnames';
import doctorsService from 'services/doctorsService';
import '../../assets/css/DoctorDetails.css';

const SUB_PAGE_SIZE = 10;

/**
 * @component DoctorDetailsModal
 * @description Modal that lazily fetches a doctor's associated practices.
 * Implements server-side search and server-side pagination for sub-documents.
 */
const DoctorDetailsModal = ({ isOpen, toggle, doctor, t }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  const [details, setDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [subPages, setSubPages] = useState({});
  const [subLoading, setSubLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounce logic for the server-side search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500); // 500ms delay to prevent API flooding
    return () => clearTimeout(timer);
  }, [searchInput]);

  /**
   * Fetches the initial details payload.
   * If a searchQuery is present, the server filters all practices' subdocuments globally.
   */
  const fetchInitialDetails = useCallback(async (searchQuery = '') => {
    if (!doctor?.auth_user_id) return;

    setLoadingDetails(true);
    setError(null);

    try {
      const response = await doctorsService.getDoctorDetails(
        doctor.auth_user_id,
        0,
        50, // Limit for practices
        0,
        SUB_PAGE_SIZE,
        searchQuery
      );

      const payload = response?.data?.data;
      const resultData = Array.isArray(payload) ? payload[0] : payload?.data?.[0];

      if (resultData?.practices) {
        setDetails(resultData.practices);
        const initialPages = {};
        resultData.practices.forEach((_, idx) => {
          initialPages[idx] = 0;
        });
        setSubPages(initialPages);
        setActiveTab(0); // Reset to first tab on new search
      } else {
        setDetails([]);
      }
    } catch (e) {
      setError(t('error-loading-details'));
    } finally {
      setLoadingDetails(false);
    }
  }, [doctor, t]);

  // Trigger global fetch when modal opens OR when debounced search term changes
  useEffect(() => {
    if (isOpen) {
      fetchInitialDetails(debouncedSearch);
    } else {
      // Reset states when closed
      setSearchInput('');
      setDebouncedSearch('');
      setDetails([]);
    }
  }, [isOpen, debouncedSearch, fetchInitialDetails]);

  /**
   * Navigates to a specific patient sub-page for the currently active practice tab.
   * Includes the debounced search term to maintain the filtered context.
   */
  const handleSubPageChange = async (newPage) => {
    setSubLoading(true);
    try {
      const response = await doctorsService.getDoctorDetails(
        doctor.auth_user_id,
        activeTab,
        1, // Fetch only the active practice
        newPage,
        SUB_PAGE_SIZE,
        debouncedSearch
      );

      const payload = response?.data?.data;
      const resultData = Array.isArray(payload) ? payload[0] : payload?.data?.[0];

      if (resultData?.practices?.[0]) {
        setDetails((prev) => {
          const updated = [...prev];
          updated[activeTab] = resultData.practices[0];
          return updated;
        });
        setSubPages((prev) => ({ ...prev, [activeTab]: newPage }));
      }
    } catch (e) {
      setError(t('error-loading-details'));
    } finally {
      setSubLoading(false);
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const getPracticeDisplayName = (practice, index) => {
    if (practice.name) return practice.name;
    if (practice.practice_name && !practice.practice_name.startsWith('Unknown')) {
      return practice.practice_name;
    }
    return `${t('practice')} ${index + 1}`;
  };

  const activePractice = details[activeTab];
  // Data is already filtered by the server, so we map it directly
  const displayPatients = activePractice?.patients || [];
  
  const currentSubPage = subPages[activeTab] ?? 0;
  const totalSubPages = activePractice?.total_patients
    ? Math.ceil(activePractice.total_patients / SUB_PAGE_SIZE)
    : 0;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="doctor-details-modal">
      <ModalHeader toggle={toggle}>
        {t('modal-title')}: {doctor?.firstName} {doctor?.lastName}
      </ModalHeader>

      <ModalBody className="doctor-details-content">
        <div className="mb-4">
          <h5>
            <Badge color="info" className="mr-2">{doctor?.email}</Badge>
            <small className="text-muted">ID: {doctor?.auth_user_id}</small>
          </h5>
        </div>

        {loadingDetails ? (
          <div className="text-center p-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <div className="text-center p-5 text-danger">{error}</div>
        ) : details.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <i className="fa fa-folder-open fa-3x mb-3" />
            <p>{t('no-practices-found') || 'No practices linked.'}</p>
          </div>
        ) : (
          <>
            <Nav tabs className="practice-nav-tabs">
              {details.map((practice, index) => (
                <NavItem key={index}>
                  <NavLink
                    className={classnames({ active: activeTab === index })}
                    onClick={() => toggleTab(index)}
                  >
                    {getPracticeDisplayName(practice, index)}
                    <Badge color="secondary" pill className="ml-2">
                      {practice.total_patients ?? (practice.patients ? practice.patients.length : 0)}
                    </Badge>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              {details.map((practice, index) => (
                <TabPane tabId={index} key={index}>
                  <Row>
                    <Col md="12">
                      <div className="bg-light p-3 mb-3 rounded border">
                        <Row>
                          <Col md="6">
                            <p className="mb-1">
                              <strong>{t('practice-name')}:</strong>{' '}
                              {practice.name || practice.practice_name}
                            </p>
                            <p className="mb-1">
                              <strong>{t('address')}:</strong> {practice.street}{' '}
                              {practice.postal_code} {practice.city}
                            </p>
                          </Col>
                          <Col md="6">
                            <p className="mb-1">
                              <strong>{t('contact')}:</strong> {practice.contact_person}
                            </p>
                            <p className="mb-1">
                              <strong>ID:</strong> {practice.practiceId}
                            </p>
                          </Col>
                        </Row>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-muted small">
                          {t('showing')} {displayPatients.length} {t('of')} {practice.total_patients ?? 0} {t('records')}
                          {debouncedSearch && ` (filtered by "${debouncedSearch}")`}
                        </span>
                        <div style={{ width: '300px' }}>
                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>
                                <i className="fa fa-search" />
                              </InputGroupText>
                            </InputGroupAddon>
                            <Input
                              placeholder={t('search-patient') || 'Search globally...'}
                              value={searchInput}
                              onChange={(e) => setSearchInput(e.target.value)}
                            />
                          </InputGroup>
                        </div>
                      </div>

                      {/* Display elegant empty state if no patients match or exist */}
                      {practice.total_patients === 0 ? (
                        <div className="text-center py-5 text-muted">
                          <i className="fa fa-users fa-3x mb-3" />
                          <p>
                            {debouncedSearch 
                              ? 'No patients found matching your search in this practice.' 
                              : 'No patients linked to this practice.'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="patient-list-container">
                            {subLoading ? (
                              <div className="text-center py-4">
                                <Spinner size="sm" color="primary" />
                              </div>
                            ) : (
                              <Table striped hover responsive size="sm">
                                <thead className="text-primary">
                                  <tr>
                                    <th>{t('patient-name')}</th>
                                    <th>{t('patient-email')}</th>
                                    <th className="text-right">{t('patient-auth-id')}</th>
                                    <th className="text-center">{t('status')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {displayPatients.map((patient, pIdx) => (
                                    <tr key={pIdx} className={patient.is_duplicate ? 'text-danger' : ''}>
                                      <td>
                                        <i
                                          className={`fa ${patient.is_duplicate ? 'fa-files-o' : 'fa-user'} mr-2`}
                                        />
                                        {patient.firstName} {patient.lastName}
                                         <br />
                                        <small className="text-muted">ID: {patient._id}</small>
                                      </td>
                                      <td>{patient.email || '-'}</td>
                                      <td className="text-right small">{patient.auth_user_id}</td>
                                      <td className="text-center">
                                        {patient.is_duplicate && (
                                          <Badge color="danger" pill>Duplicate</Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            )}
                          </div>

                          {totalSubPages > 1 && (
                            <div className="d-flex justify-content-center mt-3 align-items-center">
                              <Button
                                size="sm"
                                color="secondary"
                                disabled={currentSubPage === 0 || subLoading}
                                onClick={() => handleSubPageChange(currentSubPage - 1)}
                              >
                                <i className="fa fa-chevron-left" />
                              </Button>
                              <span className="mx-3 small">
                                Page {currentSubPage + 1} of {totalSubPages}
                              </span>
                              <Button
                                size="sm"
                                color="secondary"
                                disabled={currentSubPage >= totalSubPages - 1 || subLoading}
                                onClick={() => handleSubPageChange(currentSubPage + 1)}
                              >
                                <i className="fa fa-chevron-right" />
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </Col>
                  </Row>
                </TabPane>
              ))}
            </TabContent>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t('btn-close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default translate('userList')(DoctorDetailsModal);